// The facility overview's KPI band as data. Each linked tile's number is
// computed with the SAME predicate the list it links to filters by
// (tiendasList.reporteOf, observacionesList.isCritSinAtender, isEntryOpen),
// so the tile and the table it opens always agree (kpiParity.test.ts).

import type { FacilityDashboardResponse, RecipeMeResponse } from "@bitacorapro/api-types";
import { isEntryOpen, type EstadoColumnKey } from "../postventa/entryThread";
import {
  CRIT_SEVERITY,
  isCritSinAtender,
  observacionesHref,
  SIN_ATENDER_PLAZOS,
} from "./observacionesList";
import { reporteOf, tiendasHref } from "./tiendasList";

/** The Estado columns that together are exactly isEntryOpen: every kind
 *  but conforme («cerradas») and rejected («anuladas»). */
export const OPEN_ESTADOS: readonly EstadoColumnKey[] = ["abiertas", "asignadas", "resueltas"];

export type FacilityKpis = {
  /** % of the brand's stores with a report in the last 30 days. */
  coverage: number;
  visitedRecently: number;
  total: number;
  /** Server-side open count (sum of the stores' openFindingsCount): the
   *  tile's value. */
  openObs: number;
  /** The same count derived from the entries with isEntryOpen. The tile
   *  only links when both agree. */
  openEntriesCount: number;
  critWithoutSla: number;
  critSinAtenderIds: string[];
  avgScore: number | null;
  avgClose: number | null;
  sevCounts: Record<string, number>;
};

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/** `brand` is a brand id or "all". Store tiles and entry tiles narrow by
 *  the same brand (decisión founder 2026-10-23: críticas filtradas por
 *  marca); the severity hint and average close days stay org-wide. */
export function computeFacilityKpis(
  data: FacilityDashboardResponse,
  recipe: RecipeMeResponse | undefined,
  nowMs: number,
  brand: string
): FacilityKpis {
  const assets = brand === "all" ? data.byAsset : data.byAsset.filter((a) => a.brandId === brand);
  const brandOf = new Map(data.byAsset.map((a) => [a.id, a.brandId]));
  const entries =
    brand === "all" ? data.entries : data.entries.filter((e) => brandOf.get(e.assetId) === brand);

  const total = assets.length;
  const visitedRecently = assets.filter((a) => reporteOf(a.lastVisit, nowMs) === "hasta30").length;
  const coverage = total > 0 ? Math.round((visitedRecently / total) * 100) : 0;
  const openObs = assets.reduce((s, a) => s + a.openFindingsCount, 0);
  const openEntriesCount = entries.filter((e) => isEntryOpen(e, recipe)).length;
  const critSinAtenderIds = entries
    .filter((e) => isCritSinAtender(e, recipe, nowMs))
    .map((e) => e.id);
  const avgScore = average(assets.map((a) => a.score).filter((v): v is number => v !== null));
  const avgClose = average(
    data.byProvider.map((p) => p.avgCloseDays).filter((v): v is number => v !== null)
  );
  return {
    coverage,
    visitedRecently,
    total,
    openObs,
    openEntriesCount,
    critWithoutSla: critSinAtenderIds.length,
    critSinAtenderIds,
    avgScore,
    avgClose,
    sevCounts: data.bySeverity,
  };
}

export type KpiHrefs = {
  coverage: string | undefined;
  open: string | undefined;
  crit: string | undefined;
};

/** Where each tile links, built through the lists' own URL writers (never
 *  literals). A tile at 0 is not a link, «Observaciones abiertas» links
 *  only while the entries agree with the server count, Score never links,
 *  and every link carries the selected brand. */
export function kpiHrefs(kpis: FacilityKpis, brand: string): KpiHrefs {
  const marca = brand === "all" ? [] : [brand];
  return {
    coverage:
      kpis.visitedRecently > 0 && kpis.coverage > 0
        ? tiendasHref({ reporte: ["hasta30"], marca })
        : undefined,
    open:
      kpis.openObs > 0 && kpis.openEntriesCount === kpis.openObs
        ? observacionesHref({ estado: [...OPEN_ESTADOS], marca })
        : undefined,
    crit:
      kpis.critWithoutSla > 0
        ? observacionesHref({ severidad: [CRIT_SEVERITY], plazo: [...SIN_ATENDER_PLAZOS], marca })
        : undefined,
  };
}
